import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useBusinessProfile, type BusinessProfile } from "../lib/tenant-api";
import { apiFetch } from "../lib/api";
import {
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

interface DayHours {
  open: boolean;
  start: string;
  end: string;
}

type HoursState = Record<string, DayHours>;

function emptyHours(): HoursState {
  return Object.fromEntries(
    DAYS.map((d) => [
      d.key,
      { open: false, start: "09:00", end: "18:00" } as DayHours,
    ]),
  );
}

function parseHoursJson(json: string | null): HoursState {
  const out = emptyHours();
  if (!json) return out;
  try {
    const parsed = JSON.parse(json) as Record<
      string,
      Array<{ open: string; close: string }>
    >;
    for (const day of DAYS) {
      const slots = parsed[day.key];
      if (slots && slots.length > 0) {
        out[day.key] = {
          open: true,
          start: slots[0].open ?? "09:00",
          end: slots[0].close ?? "18:00",
        };
      }
    }
  } catch {
    /* keep defaults */
  }
  return out;
}

function serializeHours(state: HoursState): string {
  const result: Record<string, Array<{ open: string; close: string }>> = {};
  for (const day of DAYS) {
    const h = state[day.key];
    result[day.key] = h.open ? [{ open: h.start, close: h.end }] : [];
  }
  return JSON.stringify(result);
}

export default function Settings() {
  const profile = useBusinessProfile();
  if (profile.isLoading) return <LoadingRow />;
  if (profile.error)
    return <ErrorBanner message={(profile.error as Error).message} />;
  if (!profile.data) return null;
  return <SettingsForm initial={profile.data} />;
}

function parseOriginsForEditor(json: string | null): string {
  if (!json) return "";
  try {
    const arr = JSON.parse(json);
    if (Array.isArray(arr)) return arr.map((s) => String(s)).join("\n");
  } catch {
    /* fall through */
  }
  return "";
}

/** Social links edit as "platform: url" lines (e.g. "instagram: https://...").
 *  Stored as a JSON object keyed by platform. */
function parseSocialLinksForEditor(json: string | null): string {
  if (!json) return "";
  try {
    const obj = JSON.parse(json);
    if (obj && typeof obj === "object" && !Array.isArray(obj)) {
      return Object.entries(obj)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join("\n");
    }
  } catch {
    /* fall through */
  }
  return "";
}

function serializeSocialLinksFromEditor(
  text: string,
): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const line of text.split(/\n+/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Split on the FIRST colon only — the URL value contains "https:".
    const idx = trimmed.indexOf(":");
    if (idx <= 0) continue;
    const key = trimmed.slice(0, idx).trim();
    const url = trimmed.slice(idx + 1).trim();
    if (key && url) out[key] = url;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function serializeOriginsFromEditor(text: string): string[] | null {
  const lines = text
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  return lines;
}

function SettingsForm({ initial }: { initial: BusinessProfile }) {
  const qc = useQueryClient();
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://receptionist.thynra.com";
  const [name, setName] = useState(initial.name);
  const [website, setWebsite] = useState(initial.website ?? "");
  const [industry, setIndustry] = useState(initial.industry ?? "");
  const [greeting, setGreeting] = useState(initial.greeting);
  const [voicePersona, setVoicePersona] = useState(initial.voice_persona ?? "");
  const [receptionistName, setReceptionistName] = useState(
    initial.receptionist_name ?? "",
  );
  const [phoneNumber, setPhoneNumber] = useState(initial.phone_number ?? "");
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [widgetEnabled, setWidgetEnabled] = useState(initial.widget_enabled === 1);
  const [widgetVoiceEnabled, setWidgetVoiceEnabled] = useState(
    initial.widget_voice_enabled === 1,
  );
  const [widgetOriginsText, setWidgetOriginsText] = useState(
    parseOriginsForEditor(initial.widget_allowed_origins),
  );
  const [hours, setHours] = useState<HoursState>(() =>
    parseHoursJson(initial.business_hours),
  );
  const [qualification, setQualification] = useState(
    initial.qualification_criteria ?? "",
  );
  const [bookingPolicy, setBookingPolicy] = useState(
    initial.booking_policy ?? "",
  );
  const [handoffEmail, setHandoffEmail] = useState(initial.handoff_email ?? "");
  const [handoffPhone, setHandoffPhone] = useState(initial.handoff_phone ?? "");
  const [voice, setVoice] = useState(initial.voice ?? "");
  const [calendlyUrl, setCalendlyUrl] = useState(initial.calendly_url ?? "");
  const [pricingUrl, setPricingUrl] = useState(initial.pricing_url ?? "");
  const [socialLinksText, setSocialLinksText] = useState(
    parseSocialLinksForEditor(initial.social_links),
  );
  const [brandColor, setBrandColor] = useState(initial.brand_color ?? "");
  const [logoUrl, setLogoUrl] = useState(initial.logo_url ?? "");
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Sync local state if the server data changes (e.g. operator switched
  // active business). Resets the form to whatever the new active business
  // currently looks like.
  useEffect(() => {
    setName(initial.name);
    setWebsite(initial.website ?? "");
    setIndustry(initial.industry ?? "");
    setGreeting(initial.greeting);
    setVoicePersona(initial.voice_persona ?? "");
    setReceptionistName(initial.receptionist_name ?? "");
    setPhoneNumber(initial.phone_number ?? "");
    setPhoneNumberError(null);
    setWidgetEnabled(initial.widget_enabled === 1);
    setWidgetVoiceEnabled(initial.widget_voice_enabled === 1);
    setWidgetOriginsText(parseOriginsForEditor(initial.widget_allowed_origins));
    setHours(parseHoursJson(initial.business_hours));
    setQualification(initial.qualification_criteria ?? "");
    setBookingPolicy(initial.booking_policy ?? "");
    setHandoffEmail(initial.handoff_email ?? "");
    setHandoffPhone(initial.handoff_phone ?? "");
    setVoice(initial.voice ?? "");
    setCalendlyUrl(initial.calendly_url ?? "");
    setPricingUrl(initial.pricing_url ?? "");
    setSocialLinksText(parseSocialLinksForEditor(initial.social_links));
    setBrandColor(initial.brand_color ?? "");
    setLogoUrl(initial.logo_url ?? "");
  }, [initial.id]);

  const E164 = /^\+[1-9]\d{1,14}$/;
  const save = useMutation({
    mutationFn: () => {
      const trimmedPhone = phoneNumber.trim();
      if (trimmedPhone && !E164.test(trimmedPhone)) {
        const msg =
          "Receptionist phone number must be E.164 (e.g. +15551234567)";
        setPhoneNumberError(msg);
        throw new Error(msg);
      }
      setPhoneNumberError(null);
      const originsArr = serializeOriginsFromEditor(widgetOriginsText);
      return apiFetch("/api/tenant/business", {
        method: "PATCH",
        body: JSON.stringify({
          name,
          website: website || null,
          industry: industry || null,
          greeting,
          voice_persona: voicePersona || null,
          receptionist_name: receptionistName,
          phone_number: trimmedPhone || null,
          business_hours: serializeHours(hours),
          qualification_criteria: qualification || null,
          booking_policy: bookingPolicy || null,
          handoff_email: handoffEmail || null,
          handoff_phone: handoffPhone || null,
          voice: voice || null,
          calendly_url: calendlyUrl.trim() || null,
          pricing_url: pricingUrl.trim() || null,
          social_links: serializeSocialLinksFromEditor(socialLinksText),
          brand_color: brandColor.trim() || null,
          logo_url: logoUrl.trim() || null,
          widget_enabled: widgetEnabled ? 1 : 0,
          widget_voice_enabled: widgetVoiceEnabled ? 1 : 0,
          widget_allowed_origins: originsArr,
        }),
      });
    },
    onSuccess: () => {
      setSavedAt(new Date());
      qc.invalidateQueries({ queryKey: ["tenant"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  return (
    <form
      className="space-y-8 max-w-3xl"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Edit how the receptionist introduces itself and behaves.
        </p>
      </div>

      {save.error && (
        <ErrorBanner message={(save.error as Error).message} />
      )}

      <Section title="Identity" subtitle="Who is the caller talking to?">
        <Field label="Receptionist name *" hint="The first-person name the AI introduces itself with (e.g. 'Sofia').">
          <Input value={receptionistName} onChange={setReceptionistName} required />
        </Field>
        <Field label="Business name *">
          <Input value={name} onChange={setName} required />
        </Field>
        <Field label="Industry">
          <Input value={industry} onChange={setIndustry} />
        </Field>
        <Field label="Website">
          <Input value={website} onChange={setWebsite} placeholder="https://" />
        </Field>
      </Section>

      <Section
        title="Links"
        subtitle="Structured URLs the receptionist can drop into emails — addressable as {{business.calendly_url}}, {{business.pricing_url}} and {{business.social_links.instagram}} in templates."
      >
        <Field label="Calendly URL" hint="Scheduling link sent to callers who want to book a call.">
          <Input value={calendlyUrl} onChange={setCalendlyUrl} placeholder="https://calendly.com/…" />
        </Field>
        <Field label="Pricing URL" hint="Public pricing page.">
          <Input value={pricingUrl} onChange={setPricingUrl} placeholder="https://" />
        </Field>
        <Field
          label="Social links"
          hint='One per line as "platform: url", e.g. "instagram: https://instagram.com/acme". Shown in the email footer.'
        >
          <Textarea value={socialLinksText} onChange={setSocialLinksText} rows={3} />
        </Field>
      </Section>

      <Section
        title="Email branding"
        subtitle="Outgoing emails are wrapped in a branded layout: colored header with your logo (or name), footer with your contact details and social links."
      >
        <Field label="Brand color" hint="Hex color for the email header band, e.g. #0ea5e9. Empty = neutral dark.">
          <Input value={brandColor} onChange={setBrandColor} placeholder="#0ea5e9" />
        </Field>
        <Field label="Logo URL" hint="Absolute https image URL, shown at ~36px tall in the header. Empty = business name as text.">
          <Input value={logoUrl} onChange={setLogoUrl} placeholder="https://" />
        </Field>
      </Section>

      <Section
        title="Embeddable widget"
        subtitle='Drop a chat (and optional voice) widget on a customer site with one <script> tag. Off by default — flip "Enable widget" and add their domain to the allowlist before sharing the snippet.'
      >
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1"
            checked={widgetEnabled}
            onChange={(e) => setWidgetEnabled(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-medium text-slate-700">Enable widget</span>
            <span className="block text-xs text-slate-500">
              Master switch. When off, /api/chat/start returns 403 even for
              whitelisted origins.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1"
            checked={widgetVoiceEnabled}
            disabled={!widgetEnabled}
            onChange={(e) => setWidgetVoiceEnabled(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-medium text-slate-700">
              Enable voice call button
            </span>
            <span className="block text-xs text-slate-500">
              Adds a "📞 Call" button to the widget header. Browser audio
              bridges to the receptionist via WebRTC — no PSTN dial, no second
              phone number needed. Requires the TWILIO_WIDGET_TWIML_APP_SID
              secret to be set on the server.
            </span>
          </span>
        </label>
        <Field
          label="Allowed origins"
          hint='One per line. Each must be a full origin (https://example.com, no trailing path). Use "*" alone to allow any origin (only for demos / Thynra-owned sites). Empty list disables the widget even if "Enable widget" is on.'
        >
          <Textarea
            value={widgetOriginsText}
            onChange={setWidgetOriginsText}
            rows={4}
          />
        </Field>
        <Field
          label="Embed snippet"
          hint="Paste this in the customer's site, before </body>."
        >
          <pre className="text-xs bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
{`<script src="${origin}/public/widget.js"
        data-endpoint="${origin}"
        data-business-id="${initial.id}"
        data-title="${initial.name}"
        data-accent="#0f766e"></script>`}
          </pre>
        </Field>
      </Section>

      <Section
        title="Telephony"
        subtitle="Inbound routing key. The Twilio number that this business answers on."
      >
        <Field
          label="Receptionist phone number"
          hint="E.164 format, e.g. +15551234567. Inbound calls to this number route to this business via /voice/inbound. Required for the Voice test page; if unset, calls fall back to the platform default business."
        >
          <Input
            value={phoneNumber}
            onChange={(v) => {
              setPhoneNumber(v);
              if (phoneNumberError) setPhoneNumberError(null);
            }}
            placeholder="+15551234567"
          />
          {phoneNumberError && (
            <p className="mt-1 text-xs text-red-600">{phoneNumberError}</p>
          )}
        </Field>
      </Section>

      <Section
        title="Greeting & persona"
        subtitle="The voice and tone the receptionist uses."
      >
        <Field
          label="Greeting"
          hint="Style reference for the opening line — the receptionist will vary the wording each call, not read this verbatim. Keep it short and natural; identify the business by name."
        >
          <Textarea value={greeting} onChange={setGreeting} rows={3} />
        </Field>
        <Field
          label="Persona / tone overrides"
          hint="1–4 sentences describing the agent's mannerisms, formality level, anything specific to this business."
        >
          <Textarea value={voicePersona} onChange={setVoicePersona} rows={4} />
        </Field>
        <Field label="Voice (OpenAI Realtime)" hint="alloy / ash / ballad / coral / echo / nova / sage / shimmer / verse — leave blank for env default.">
          <Input value={voice} onChange={setVoice} placeholder="nova" />
        </Field>
      </Section>

      <Section title="Hours" subtitle="When is the business open?">
        <HoursEditor hours={hours} onChange={setHours} />
      </Section>

      <Section
        title="Qualification & booking"
        subtitle="How the receptionist decides who's a real lead and how to schedule."
      >
        <Field
          label="Qualification criteria"
          hint="Free-form: who's a good fit, who isn't."
        >
          <Textarea
            value={qualification}
            onChange={setQualification}
            rows={4}
          />
        </Field>
        <Field
          label="Booking policy"
          hint="How appointments work — durations, lead times, cancellation rules."
        >
          <Textarea
            value={bookingPolicy}
            onChange={setBookingPolicy}
            rows={4}
          />
        </Field>
      </Section>

      <Section
        title="Operator notifications"
        subtitle="Where ops alerts (handoffs, leads, bookings, callbacks) go."
      >
        <Field label="Handoff email *">
          <Input
            type="email"
            value={handoffEmail}
            onChange={setHandoffEmail}
            required
          />
        </Field>
        <Field label="Handoff phone (optional)">
          <Input
            value={handoffPhone}
            onChange={setHandoffPhone}
            placeholder="+1..."
          />
        </Field>
      </Section>

      <div className="flex items-center gap-4 sticky bottom-0 bg-slate-50/95 backdrop-blur py-4 border-t border-slate-200 -mx-6 md:-mx-8 px-6 md:px-8">
        <button
          type="submit"
          disabled={save.isPending}
          className="px-5 py-2.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        {savedAt && (
          <span className="text-sm text-emerald-700">
            ✓ Saved at {savedAt.toLocaleTimeString()}
          </span>
        )}
      </div>
    </form>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="font-semibold text-slate-900">{title}</h2>
      {subtitle && (
        <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
      )}
      <div className="mt-4 space-y-4 bg-white rounded-xl border border-slate-200 p-5">
        {children}
      </div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({
  value,
  onChange,
  type = "text",
  placeholder,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
    />
  );
}

function Textarea({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
    />
  );
}

function HoursEditor({
  hours,
  onChange,
}: {
  hours: HoursState;
  onChange: (h: HoursState) => void;
}) {
  return (
    <div className="space-y-2">
      {DAYS.map((d) => {
        const h = hours[d.key];
        return (
          <div key={d.key} className="flex items-center gap-3">
            <label className="flex items-center gap-2 w-32">
              <input
                type="checkbox"
                checked={h.open}
                onChange={(e) =>
                  onChange({
                    ...hours,
                    [d.key]: { ...h, open: e.target.checked },
                  })
                }
              />
              <span className="text-sm text-slate-700">{d.label}</span>
            </label>
            {h.open && (
              <>
                <input
                  type="time"
                  value={h.start}
                  onChange={(e) =>
                    onChange({
                      ...hours,
                      [d.key]: { ...h, start: e.target.value },
                    })
                  }
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                />
                <span className="text-slate-400">to</span>
                <input
                  type="time"
                  value={h.end}
                  onChange={(e) =>
                    onChange({
                      ...hours,
                      [d.key]: { ...h, end: e.target.value },
                    })
                  }
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
